import Vue from 'vue';
import { Configuration, PublicClientApplication, RedirectRequest, SilentRequest, InteractionRequiredAuthError } from '@azure/msal-browser';
import { AccountInfo, AuthenticationResult, EndSessionRequest, LogLevel } from '@azure/msal-common';
import { GraphService } from '@/plugins/graph.service';
import { Event, EventBus } from '@/plugins/bus';
import { Toast } from '@/plugins/toast';
import CONFIG from '@/config';
import JwtHelper from '@/lib/JwtHelper';

/**
 * Configuration class for @azure/msal-browser:
 * https://azuread.github.io/microsoft-authentication-library-for-js/ref/msal-browser/modules/_src_config_configuration_.html
 */
const MSAL_CONFIG: Configuration = {
  auth: {
    clientId: CONFIG.azureAd.clientId,
    authority: 'https://login.microsoftonline.com/e416ab61-afc4-415b-9a45-cec1678a0f7d',
    redirectUri: CONFIG.webBaseUrl,
    navigateToLoginRequestUrl: true
  },
  cache: {
    cacheLocation: 'localStorage', // Changed from sessionStorage to localStorage for persistence
    storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
  },
  system: {
    tokenRenewalOffsetSeconds: 60 * 15 // 15 Minutes
  }
};

/**
 * AuthModule for application - handles authentication in app.
 */
export class AuthModule {

  public account: AccountInfo; // https://azuread.github.io/microsoft-authentication-library-for-js/ref/msal-common/modules/_src_account_accountinfo_.html
  public user: any;
  public idToken: string;
  public idTokenExpiry: Date;

  private myMSALObj: PublicClientApplication; // https://azuread.github.io/microsoft-authentication-library-for-js/ref/msal-browser/classes/_src_app_publicclientapplication_.publicclientapplication.html
  private loginRequest: RedirectRequest; // https://azuread.github.io/microsoft-authentication-library-for-js/ref/msal-browser/modules/_src_request_redirectrequest_.html
  private loginSilentRequest: SilentRequest;
  private profileRedirectRequest: RedirectRequest;
  private silentProfileRequest: SilentRequest; // https://azuread.github.io/microsoft-authentication-library-for-js/ref/msal-browser/modules/_src_request_silentrequest_.html
  private graphService: GraphService;
  private accessToken: string;
  private roles: string[];

  constructor() {
    this.myMSALObj = new PublicClientApplication(MSAL_CONFIG);
    this.loadAuthModule();
    this.account = null;
    this.setRequestObjects();
    this.graphService = new GraphService();
  }

  public login(): void {
    // noinspection JSIgnoredPromiseFromCall - Promise is not returned beacuse user is navigated away from page=
    this.myMSALObj.loginRedirect(this.loginRequest);
  }

  public getToken(): Promise<string | void> {
    if (!this.account) {
      this.account = this.getAccount();
    }

    if (!this.account) {
      return Promise.reject('No account found');
    }

    this.loginSilentRequest = {
      ...this.silentProfileRequest,
      account: this.account
    };
    return this.myMSALObj.acquireTokenSilent(this.loginSilentRequest).then((response: AuthenticationResult) => {
      return this.handleLoginResponse(response);
    }).catch(error => {
      if (error instanceof InteractionRequiredAuthError) {
        // Only redirect to login if interaction is required
        this.login();
      }
      return Promise.reject(error);
    });
  }

  public logout(): void {
    const logOutRequest: EndSessionRequest = {
      account: this.getAccount(),
      postLogoutRedirectUri: CONFIG.webBaseUrl
    };
    // noinspection JSIgnoredPromiseFromCall - Promise is not returned beacuse user is navigated away from page
    this.myMSALObj.logoutRedirect(logOutRequest);
  }

  public checkRole(role: string): boolean {
    if (!this.idToken) {
      return false;
    }
    if (!this.roles) {
      let decodedToken = JwtHelper.parse(this.idToken);
      if (decodedToken == null) {
        return false;
      }
      this.roles = decodedToken.roles;
    }
    let hasRole = this.roles.indexOf(role) > -1;
    return hasRole;
  }

  /**
   * Checks whether we are in the middle of a redirect and handles state accordingly. Only required for redirect flows.
   *
   * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/initialization.md#redirect-apis
   */
  private loadAuthModule(): void {
    this.myMSALObj.handleRedirectPromise().then((response: AuthenticationResult) => {
      this.handleLoginResponse(response);
    }).catch((error: any) => {
      if (error && error.errorMessage) {
        this.handleLoginError(error);
      }
    }).finally(() => {
      // After handling redirect, try to restore session if no account is set
      if (!this.account) {
        const account = this.getAccount();
        if (account) {
          this.account = account;
          // Try to acquire token silently
          this.loginSilentRequest = {
            ...this.silentProfileRequest,
            account: this.account
          };
          this.myMSALObj.acquireTokenSilent(this.loginSilentRequest)
            .then((response: AuthenticationResult) => {
              this.handleLoginResponse(response);
            })
            .catch(() => {
              // If silent token acquisition fails, don't redirect immediately
              // Let the application handle the authentication state
              //console.log('Silent token acquisition failed');
            });
        }
      }
    });
  }

  /**
   * Initialize request objects used by this AuthModule.
   */
  private setRequestObjects(): void {
    const account = this.getAccount();

    this.loginRequest = {
      scopes: ['openid', 'profile', 'User.Read'],
      redirectStartPage: CONFIG.webBaseUrl,
        account
    };

    this.profileRedirectRequest = {
      scopes: ['openid', 'profile', 'User.Read'],
      redirectStartPage: CONFIG.webBaseUrl,
        account
    };

    this.silentProfileRequest = {
      scopes: ['openid', 'profile', 'User.Read'],
        account,
      forceRefresh: false
    };
  }

  /**
   * Calls getAllAccounts and determines the correct account to sign into, currently defaults to first account found in cache.
   * TODO: Add account chooser code
   *
   * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-common/docs/Accounts.md
   */
  private getAccount(): AccountInfo | null {
      const currentAccounts = this.myMSALObj.getAllAccounts();
      if (currentAccounts === null || currentAccounts.length === 0) {
          return null;
      }

      if (currentAccounts.length > 1) {
          // TODO: Choose account
          return currentAccounts[0];
      } else if (currentAccounts.length === 1) {
          return currentAccounts[0];
      }
  }

  /**
   * Handles the response from a popup or redirect. If response is null, will check if we have any accounts and attempt to sign in.
   * @param response
   */
  private handleLoginResponse(response: AuthenticationResult | null): string {
    if (response !== null) {
      this.idToken = response.idToken;
      this.idTokenExpiry = response.expiresOn;
      this.accessToken = response.accessToken;
      this.account = response.account;
      JwtHelper.checkTokenDetails(this.idToken);
    } else {
      let thisAccount = this.getAccount();
      this.account = thisAccount;
    }

    if (this.accessToken) {
      this.graphService.getUserInfo(this.accessToken).then(data => {
        this.user = data;
        EventBus.$emit(Event.UserInfoReceived);
      },
      );
    }

    return this.idToken;
  }

  private handleLoginError(error: any) {
    const errorCode = error.errorMessage.split(':')[0];
    switch (errorCode) {
      case 'AADSTS50105':
        // The signed in user is not assigned to a role for the application
        Toast.error('Your account is not associated with a GateKeeper role.<br/>Please contact Fleet to be assigned a role.');
        break;
      default:
        // Other/non-specific login error
        Toast.error('Unable to log in. Please check your account and try again.<br/>Please contact Fleet if this continues.');
        break;
    }
    //Sign the user out
    setTimeout(() => {
      this.logout();
    },
      5000);
  }
}

const authService = new AuthModule();

Vue.use({
  install(vue) {
    vue.prototype.$authService = authService;
  }
});

export const AuthService = authService;
